import React, { useMemo } from 'react'
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import styled, { keyframes } from 'styled-components'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import {
  BASELINE_COLOR,
  CHART_COLOR,
} from 'features/trend-detection/components/issue-constants'
import {
  computeShareTimeseries,
  formatMultiplierText,
  transformChartToTimeseries,
} from 'features/trend-detection/components/mini-timeseries-utils'
import type { Chart } from 'features/trend-detection/types'

interface MiniTimeseriesProps {
  chart: Chart | null | undefined
  loading: boolean
  multiplier: number | null | undefined
  recordCount?: number
  zeroLabel?: string
  chartHeight?: number
  projectTotalChart?: Chart | null
  baselineShare?: number | null
  container?: boolean
}

export const MiniTimeseries: React.FC<MiniTimeseriesProps> = React.memo(
  ({
    chart,
    loading,
    multiplier,
    recordCount,
    zeroLabel,
    chartHeight = 80,
    projectTotalChart,
    baselineShare,
    container = true,
  }) => {
    const { primary, secondary } = formatMultiplierText(
      multiplier,
      recordCount,
      zeroLabel,
    )
    const timeseriesPoints = useMemo(() => {
      if (!chart || !chart.data?.length) return []
      if (projectTotalChart && projectTotalChart.data?.length > 0) {
        return computeShareTimeseries(chart, projectTotalChart)
      }
      return transformChartToTimeseries(chart)
    }, [chart, projectTotalChart])

    return (
      <MiniChartPanel $container={container}>
        <MultiplierBlock>
          <MultiplierPrimary>{primary}</MultiplierPrimary>
          <MultiplierSecondary>{secondary}</MultiplierSecondary>
        </MultiplierBlock>
        <ChartArea $height={chartHeight}>
          {loading ? (
            <SkeletonShimmer $height={chartHeight} />
          ) : timeseriesPoints.length > 0 ? (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <ComposedChart
                data={timeseriesPoints}
                margin={{ top: 10, right: 4, bottom: 0, left: 4 }}
              >
                <defs>
                  <linearGradient
                    id="miniAreaGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={CHART_COLOR}
                      stopOpacity={0.15}
                    />
                    <stop
                      offset="100%"
                      stopColor={CHART_COLOR}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="none"
                  fill="url(#miniAreaGradient)"
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={CHART_COLOR}
                  strokeWidth={2}
                  dot={false}
                />
                {baselineShare != null && isFinite(baselineShare) && (
                  <ReferenceLine
                    y={baselineShare}
                    stroke={BASELINE_COLOR}
                    strokeWidth={1}
                    strokeDasharray="4 3"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : null}
        </ChartArea>
      </MiniChartPanel>
    )
  },
)
MiniTimeseries.displayName = 'MiniTimeseries'

const MiniChartPanel = styled.div<{ $container: boolean }>`
  width: ${({ $container }) => ($container ? '280px' : '100%')};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ $container }) => ($container ? '16px' : '0')};
  border: ${({ $container }) => ($container ? `1px solid ${cardBorder}` : '0')};
  background: white;
  border-radius: ${({ $container }) => ($container ? '8px' : '0')};
`

const MultiplierBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 4px;
`

const MultiplierPrimary = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${darkBlueFontColor};
  margin: 0;
`

const MultiplierSecondary = styled.p`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.55;
  margin: 0;
`

const ChartArea = styled.div<{ $height: number }>`
  flex: 1;
  min-height: 0;
  margin-top: 8px;
`

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`

const SkeletonShimmer = styled.div<{ $height: number }>`
  width: 100%;
  height: ${(p) => p.$height}px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`
